import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { coursesApi, studentsApi, videosApi } from '../services/api';
import VideoPlayer from '../components/VideoPlayer';
import QuizButton from '../components/QuizButton';
import { 
  Play, 
  CheckCircle, 
  Clock, 
  Users, 
  Award,
  Download,
  BookOpen
} from 'lucide-react';

interface Lesson {
  id: string;
  title: string;
  description: string;
  content?: string;
  videoDescription?: string; // Posible campo para descripción del video
  instructions?: string; // Posible campo para instrucciones
  notes?: string; // Posible campo para notas
  videoUrl?: string;
  videoDuration?: number;
  order: number;
  type: 'VIDEO' | 'TEXT' | 'QUIZ';
  isPreview: boolean;
  isCompleted?: boolean;
  progress?: {
    watchTime: number;
    completed: boolean;
  };
}


interface Course {
  id: string;
  title: string;
  description: string;
  thumbnail?: string;
  category?: {
    name: string;
  };
  lessons?: Lesson[];
  modules?: Lesson[];
  progress?: number;
  certificate?: {
    id: string;
    downloadUrl: string;
  };
}

const CoursePage: React.FC = () => {
  const { id: courseId } = useParams<{ id: string }>();
  const [course, setCourse] = useState<Course | null>(null);
  const [currentLesson, setCurrentLesson] = useState<Lesson | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Debug logs removed - field analysis complete

  useEffect(() => {
    console.log('CoursePage: courseId useEffect triggered', courseId);
    if (courseId) {
      loadCourse();
    }
  }, [courseId]);

  const loadCourse = async () => {
    console.log('CoursePage: loadCourse called for courseId:', courseId);
    try {
      setLoading(true);
      setError('');
      
      if (!courseId) {
        throw new Error('Course ID is required');
      }
      
      const response = await coursesApi.getById(courseId);
      console.log('CoursePage: Full API response:', response);
      
      // FIXED: La API devuelve los datos dentro de response.data.data.course
      const courseData = response.data?.data?.course || response.data?.course || response.data;
      console.log('CoursePage: Course data extracted:', courseData);
      console.log('CoursePage: Course data keys:', Object.keys(courseData || {}));
      console.log('CoursePage: Response data structure:', JSON.stringify(response.data, null, 2));
      
      if (!courseData) {
        throw new Error('No course data received from API');
      }
      
      setCourse(courseData);
      
      // More robust lesson selection (incluir lecciones QUIZ)
      const allLessons = courseData.lessons || courseData.modules || [];
      const lessons = allLessons; // No filtrar lecciones QUIZ
      console.log('CoursePage: Available lessons/modules (all types):', lessons);
      console.log('CoursePage: Lessons count:', lessons.length);

      if (lessons.length > 0) {
        const firstIncomplete = lessons.find((l: Lesson) => !l.isCompleted);
        const initialLesson = firstIncomplete || lessons[0];
        console.log('CoursePage: Setting initial lesson:', initialLesson);
        setCurrentLesson(initialLesson);
      } else {
        console.log('CoursePage: No lessons found in course data');
        setCurrentLesson(null);
      }
      
    } catch (error: any) {
      console.error('CoursePage: Error loading course', error);
      const errorMessage = error.response?.data?.message || error.message || 'Error cargando el curso';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleLessonComplete = useCallback(async (lessonId: string) => {
    console.log('CoursePage: handleLessonComplete called for lessonId:', lessonId);
    try {
      await videosApi.updateProgress(lessonId, {
        watchTime: currentLesson?.videoDuration || 0,
        completed: true
      });
      
      if (course) {
        const allLessons = course.lessons || course.modules || [];
        const lessons = allLessons; // Incluir todas las lecciones incluidos QUIZ
        const updatedLessons = allLessons.map(l =>
          l.id === lessonId ? { ...l, isCompleted: true } : l
        );
        const completedLessons = lessons.filter(l => l.isCompleted || l.id === lessonId);
        const newProgress = (completedLessons.length / lessons.length) * 100;
        
        const updatedCourse = { 
          ...course, 
          progress: newProgress,
          ...(course.lessons ? { lessons: updatedLessons } : { modules: updatedLessons })
        };
        setCourse(updatedCourse);

        const currentIndex = lessons.findIndex(l => l.id === lessonId);
        const nextLesson = lessons[currentIndex + 1];
        if (nextLesson) {
          console.log('CoursePage: Advancing to next lesson', nextLesson);
          setCurrentLesson(nextLesson);
        }
      }
    } catch (error) {
      console.error('Error marking lesson as complete:', error);
    }
  }, [course, currentLesson]);

  const handleVideoProgress = useCallback(async (progress: { watchTime: number; completed?: boolean }) => {
    if (!currentLesson) return;
    console.log('CoursePage: handleVideoProgress called', progress);

    try {
      await videosApi.updateProgress(currentLesson.id, progress);
      
      if (progress.completed) {
        handleLessonComplete(currentLesson.id);
      }
    } catch (error) {
      console.error('Error updating video progress:', error);
    }
  }, [currentLesson, handleLessonComplete]);

  const downloadCertificate = async () => {
    try {
      const response = await studentsApi.downloadCertificate(courseId!);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `certificado-${course?.title}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading certificate:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-500/20 border-t-blue-500 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-white">Cargando curso...</p>
          <p className="text-gray-400 text-sm mt-2">Course ID: {courseId}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <div className="cyber-container p-6 max-w-md mx-auto">
          <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-red-500 text-2xl">⚠</span>
          </div>
          <h3 className="text-xl font-bold text-white mb-2">Error al cargar el curso</h3>
          <p className="text-red-400 mb-4">{error}</p>
          <button
            onClick={loadCourse}
            className="cyber-button"
          >
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  if (!course) {
    return (
      <div className="text-center py-8">
        <div className="cyber-container p-6 max-w-md mx-auto">
          <h3 className="text-xl font-bold text-white mb-2">Curso no encontrado</h3>
          <p className="text-gray-400 mb-4">El curso solicitado no existe o no tienes acceso a él.</p>
          <p className="text-gray-500 text-sm">Course ID: {courseId}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 fade-in">
      {/* Course Header */}
      <div className="cyber-container p-6">
        <div className="flex items-start gap-6">
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-white mb-2">{course.title}</h1>
            <p className="text-gray-300 mb-4">{course.description}</p>
            
            <div className="flex items-center gap-6 text-sm text-gray-400">
              <span className="flex items-center gap-1">
                <Users size={16} />
                {course.category?.name || 'Sin categoría'}
              </span>
              <span className="flex items-center gap-1">
                <Clock size={16} />
                {(course.lessons || course.modules || []).length} lecciones
              </span>
              <span className="flex items-center gap-1">
                <Award size={16} />
                {Math.round(course.progress || 0)}% completado
              </span>
            </div>

            {/* Progress Bar */}
            <div className="mt-4">
              <div className="progress-bar">
                <div 
                  className="progress-fill"
                  style={{ width: `${course.progress || 0}%` }}
                ></div>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                {Math.round(course.progress || 0)}% completado
              </p>
            </div>
          </div>

          {/* Certificate Download */}
          {(course.progress || 0) >= 100 && course.certificate && (
            <button
              onClick={downloadCertificate}
              className="cyber-button flex items-center gap-2"
            >
              <Download size={16} />
              Descargar Certificado
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Video Player */}
        <div className="lg:col-span-3">
          <div className="cyber-container p-6">
            {currentLesson ? (
              <>
                <h2 className="text-xl font-bold text-white mb-4">
                  {currentLesson.title}
                </h2>
                
                {/* Video Player */}
                {currentLesson.type === 'VIDEO' && currentLesson.videoUrl ? (
                  <div className="mb-6">
                    <VideoPlayer
                      key={currentLesson.id}
                      lessonId={currentLesson.id}
                      title={currentLesson.title}
                      videoUrl={currentLesson.videoUrl}
                      onProgress={handleVideoProgress}
                      onEnded={() => handleLessonComplete(currentLesson.id)}
                    />

                    {/* Quiz Opcional - Solo para lecciones de video */}
                    <QuizButton
                      lessonId={currentLesson.id}
                      lessonTitle={currentLesson.title}
                    />
                  </div>
                ) : currentLesson.type === 'QUIZ' ? (
                  <div className="mb-6">
                    {/* Componente de Quiz Independiente */}
                    <div className="relative bg-gradient-to-br from-orange-900/20 to-red-900/20 border-2 border-orange-500/30 rounded-lg overflow-hidden aspect-video flex items-center justify-center">
                      <div className="text-center text-orange-100">
                        <div className="w-20 h-20 bg-orange-500/20 rounded-full flex items-center justify-center mx-auto mb-4 border-2 border-orange-400/50">
                          <span className="text-3xl">🧠</span>
                        </div>
                        <h3 className="text-2xl font-bold mb-2">Quiz Interactivo</h3>
                        <p className="text-orange-200 mb-4">Esta lección contiene un quiz para evaluar tu conocimiento</p>
                        <QuizButton
                          lessonId={currentLesson.id}
                          lessonTitle={currentLesson.title}
                          isIndependentQuiz={true}
                        />
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="relative bg-black rounded-lg overflow-hidden aspect-video flex items-center justify-center mb-6">
                    <div className="text-center text-gray-400">
                      <Play size={48} className="mx-auto mb-2" />
                      <p>{currentLesson.type !== 'VIDEO' ? 'Esta lección no contiene video.' : 'Video no disponible'}</p>
                    </div>
                  </div>
                )}

                {/* Enhanced Lesson Information */}
                <div className="mb-6 space-y-4">
                  
                  {/* Module Description - Always show with professional styling */}
                  <div className="bg-gradient-to-r from-blue-500/10 to-cyan-500/10 rounded-lg p-6 border border-blue-500/20 shadow-lg backdrop-blur-sm">
                    <div className="flex items-start gap-4">
                      <div className="p-3 bg-blue-500/20 rounded-xl shadow-md">
                        <BookOpen size={24} className="text-blue-400" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-xl font-bold text-white mb-3 flex items-center gap-2">
                          📚 Descripción del Módulo
                          <span className="text-xs bg-blue-500/20 text-blue-400 px-2 py-1 rounded-full">
                            {currentLesson.order || 1}
                          </span>
                        </h3>
                        <p className="text-gray-300 leading-relaxed text-lg">
                          {currentLesson.description || "Este módulo contiene contenido educativo importante para tu aprendizaje."}
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Video/Content Description - Show for video and quiz lessons */}
                  {currentLesson.type === 'VIDEO' && (
                    <div className="bg-gradient-to-r from-purple-500/10 to-pink-500/10 rounded-lg p-6 border border-purple-500/20 shadow-lg backdrop-blur-sm">
                      <div className="flex items-start gap-4">
                        <div className="p-3 bg-purple-500/20 rounded-xl shadow-md">
                          <Play size={24} className="text-purple-400" />
                        </div>
                        <div className="flex-1">
                          <h4 className="text-xl font-bold text-white mb-3 flex items-center gap-2">
                            🎥 Información del Video
                            <span className="text-xs bg-purple-500/20 text-purple-400 px-2 py-1 rounded-full">
                              {Math.ceil((currentLesson.videoDuration || 0) / 60)} min
                            </span>
                          </h4>
                          <div className="text-gray-300 leading-relaxed">
                            {currentLesson.content ? (
                              <div className="bg-gray-800/30 rounded-lg p-4 border border-gray-700/30">
                                <div className="text-base leading-7 space-y-3">
                                  {currentLesson.content.split('\n\n').map((paragraph, index) => (
                                    <p key={index} className="text-gray-300">
                                      {paragraph.split('\n').map((line, lineIndex) => (
                                        <span key={lineIndex}>
                                          {line.replace(/(https?:\/\/[^\s]+)/g, (url) =>
                                            `<a href="${url}" target="_blank" rel="noopener noreferrer" class="text-cyan-400 hover:text-cyan-300 underline transition-colors">${url}</a>`
                                          ).split(/(<a[^>]*>.*?<\/a>)/).map((part, partIndex) =>
                                            part.startsWith('<a') ? (
                                              <span key={partIndex} dangerouslySetInnerHTML={{ __html: part }} />
                                            ) : (
                                              <span key={partIndex}>{part}</span>
                                            )
                                          )}
                                          {lineIndex < paragraph.split('\n').length - 1 && <br />}
                                        </span>
                                      ))}
                                    </p>
                                  ))}
                                </div>
                              </div>
                            ) : (
                              <div>
                                <p className="mb-2">Este video contiene material educativo importante para tu aprendizaje.</p>
                                <p className="text-purple-300">💡 Asegúrate de completar todo el video para maximizar tu comprensión.</p>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Quiz Description - Show for quiz lessons */}
                  {currentLesson.type === 'QUIZ' && (
                    <div className="bg-gradient-to-r from-orange-500/10 to-red-500/10 rounded-lg p-6 border border-orange-500/20 shadow-lg backdrop-blur-sm">
                      <div className="flex items-start gap-4">
                        <div className="p-3 bg-orange-500/20 rounded-xl shadow-md">
                          <span className="text-2xl">🧠</span>
                        </div>
                        <div className="flex-1">
                          <h4 className="text-xl font-bold text-white mb-3 flex items-center gap-2">
                            🧠 Información del Quiz
                            <span className="text-xs bg-orange-500/20 text-orange-400 px-2 py-1 rounded-full">
                              Evaluación
                            </span>
                          </h4>
                          <div className="text-gray-300 leading-relaxed">
                            {currentLesson.content || currentLesson.description ? (
                              <div className="bg-gray-800/30 rounded-lg p-4 border border-gray-700/30">
                                <div className="text-base leading-7 space-y-3">
                                  <p className="text-gray-300">
                                    {currentLesson.content || currentLesson.description}
                                  </p>
                                </div>
                              </div>
                            ) : (
                              <div>
                                <p className="mb-2">Este quiz evaluará tu comprensión de los temas tratados.</p>
                                <p className="text-orange-300">🧠 Responde todas las preguntas para completar la lección.</p>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Professional Stats Panel */}
                  <div className="bg-gradient-to-r from-gray-800/50 to-gray-900/50 rounded-lg p-6 border border-gray-700/30 shadow-lg">
                    <h5 className="text-sm font-semibold text-gray-300 mb-4 uppercase tracking-wider">
                      ℹ️ Información de la Lección
                    </h5>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                      <div className="text-center p-3 bg-white/5 rounded-lg border border-gray-700/20">
                        <Clock size={18} className="mx-auto text-blue-400 mb-2" />
                        <p className="text-xs text-gray-400 mb-1">Duración</p>
                        <p className="text-white font-bold text-sm">{currentLesson.videoDuration ? Math.ceil(currentLesson.videoDuration / 60) : '~'} min</p>
                      </div>
                      
                      <div className="text-center p-3 bg-white/5 rounded-lg border border-gray-700/20">
                        <span className={`inline-block w-3 h-3 rounded-full mx-auto mb-2 ${
                          currentLesson.type === 'VIDEO' ? 'bg-purple-500' :
                          currentLesson.type === 'TEXT' ? 'bg-green-500' :
                          'bg-orange-500'
                        }`}></span>
                        <p className="text-xs text-gray-400 mb-1">Tipo</p>
                        <p className="text-white font-bold text-sm">{
                          currentLesson.type === 'VIDEO' ? 'Video' :
                          currentLesson.type === 'TEXT' ? 'Texto' :
                          currentLesson.type === 'QUIZ' ? 'Quiz' :
                          'Tarea'
                        }</p>
                      </div>
                      
                      <div className="text-center p-3 bg-white/5 rounded-lg border border-gray-700/20">
                        {currentLesson.isCompleted ? (
                          <CheckCircle size={18} className="mx-auto text-green-400 mb-2" />
                        ) : (
                          <Clock size={18} className="mx-auto text-yellow-400 mb-2" />
                        )}
                        <p className="text-xs text-gray-400 mb-1">Estado</p>
                        <p className={`font-bold text-sm ${currentLesson.isCompleted ? 'text-green-400' : 'text-yellow-400'}`}>
                          {currentLesson.isCompleted ? 'Completado' : 'Pendiente'}
                        </p>
                      </div>
                      
                      <div className="text-center p-3 bg-white/5 rounded-lg border border-gray-700/20">
                        <span className="text-lg mb-2 block">{((course?.lessons || []).findIndex(l => l.id === currentLesson?.id) || 0) + 1}</span>
                        <p className="text-xs text-gray-400 mb-1">Lección</p>
                        <p className="text-white font-bold text-sm">de {(course?.lessons || []).length || 0}</p>
                      </div>
                      
                      <div className="text-center p-3 bg-white/5 rounded-lg border border-gray-700/20">
                        {currentLesson.isPreview ? (
                          <Play size={18} className="mx-auto text-blue-400 mb-2" />
                        ) : (
                          <BookOpen size={18} className="mx-auto text-cyan-400 mb-2" />
                        )}
                        <p className="text-xs text-gray-400 mb-1">Acceso</p>
                        <p className={`font-bold text-sm ${currentLesson.isPreview ? 'text-blue-400' : 'text-cyan-400'}`}>
                          {currentLesson.isPreview ? 'Preview' : 'Premium'}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Complete Lesson Button */}
                {!currentLesson.isCompleted && (
                  <button
                    onClick={() => handleLessonComplete(currentLesson.id)}
                    className="cyber-button flex items-center gap-2"
                  >
                    <CheckCircle size={18} />
                    Marcar como Completado
                  </button>
                )}
              </>
            ) : (
              <div className="text-center py-12">
                <Play size={48} className="mx-auto text-gray-500 mb-3" />
                <p className="text-gray-400">Selecciona una lección para comenzar</p>
              </div>
            )}
          </div>
        </div>

        {/* Lessons List */}
        <div className="lg:col-span-1">
          <div className="cyber-container p-6">
            <h3 className="text-lg font-bold text-white mb-4">Lecciones del Curso</h3>
            
            <div className="space-y-2">
              {(course.lessons || course.modules || [])
                .map((lesson, index) => (
                <button
                  key={lesson.id}
                  onClick={() => setCurrentLesson(lesson)}
                  className={`w-full text-left p-3 rounded-lg transition-all ${
                    currentLesson?.id === lesson.id
                      ? 'bg-cyan-500/20 border border-cyan-500/50'
                      : 'bg-white/5 hover:bg-white/10'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`p-1 rounded-full ${
                      lesson.isCompleted 
                        ? 'bg-green-500' 
                        : currentLesson?.id === lesson.id
                        ? 'bg-cyan-500'
                        : 'bg-gray-500'
                    }`}>
                      {lesson.isCompleted ? (
                        <CheckCircle size={16} className="text-white" />
                      ) : (
                        <span className="block w-4 h-4 text-white text-xs flex items-center justify-center">
                          {index + 1}
                        </span>
                      )}
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <h4 className={`font-medium text-sm ${
                        currentLesson?.id === lesson.id ? 'text-cyan-400' : 'text-white'
                      }`}>
                        {lesson.title}
                      </h4>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-xs text-gray-400">
                          {lesson.videoDuration ? Math.ceil(lesson.videoDuration / 60) : '~'} min
                        </span>
                        {lesson.isPreview && (
                          <span className="text-xs bg-blue-500/20 text-blue-400 px-2 py-0.5 rounded-full">
                            Preview
                          </span>
                        )}
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                          lesson.type === 'VIDEO' ? 'bg-purple-500/20 text-purple-400' :
                          lesson.type === 'TEXT' ? 'bg-green-500/20 text-green-400' :
                          lesson.type === 'QUIZ' ? 'bg-orange-500/20 text-orange-400' :
                          'bg-gray-500/20 text-gray-400'
                        }`}>
                          {lesson.type === 'QUIZ' ? 'QUIZ' : lesson.type}
                        </span>
                      </div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CoursePage;